import * as THREE from "three";
import type { Cloud } from "./formations";

/**
 * Turn arbitrary visual inputs — typed text, SVG, images — into swarm clouds.
 * Everything rasterizes to a canvas, then we sample it as either:
 *   - "outline": evenly-spaced dots tracing the shape's contour (drone-show
 *     convention — how the animator's Blender/DSS setup renders text), or
 *   - "fill": an even geometric lattice filling the interior.
 * Both give flat, distinct points that drop into the same morph/physics engine.
 */

export type RenderMode = "outline" | "fill";
type Colorize = "pixel" | "rainbow" | "pixelBright";

const _c = new THREE.Color();

type InsideFn = (i: number) => boolean;

/** Even geometric lattice over the interior. */
function collectFill(inside: InsideFn, W: number, H: number, count: number): number[] {
  let area = 0;
  for (let i = 0; i < W * H; i++) if (inside(i)) area++;
  if (area === 0) area = 1;

  let spacing = Math.max(1, Math.sqrt(area / count));
  let pts: number[] = [];
  for (let iter = 0; iter < 26; iter++) {
    pts = [];
    const cols = Math.max(1, Math.floor(W / spacing));
    const rows = Math.max(1, Math.floor(H / spacing));
    const ox = (W - (cols - 1) * spacing) / 2;
    const oy = (H - (rows - 1) * spacing) / 2;
    for (let gy = 0; gy < rows; gy++) {
      const yy = Math.round(oy + gy * spacing);
      if (yy < 0 || yy >= H) continue;
      const row = yy * W;
      for (let gx = 0; gx < cols; gx++) {
        const xx = Math.round(ox + gx * spacing);
        if (xx < 0 || xx >= W) continue;
        if (inside(row + xx)) pts.push(row + xx);
      }
    }
    if (pts.length >= count || spacing <= 1) break;
    spacing = Math.max(1, spacing * 0.9);
  }
  if (pts.length < count) {
    pts = [];
    for (let i = 0; i < W * H; i++) if (inside(i)) pts.push(i);
  }
  return pts;
}

type Pt = [number, number];

/**
 * Evenly-spaced dots tracing the contour. We chain the boundary pixels into
 * ordered loops, then resample by ARC LENGTH to exactly `count` points — so
 * spacing is uniform and there are never duplicates, at any count (the extra
 * points just sit closer, sub-pixel, like resampling the animator's path).
 */
function collectOutline(inside: InsideFn, W: number, H: number, count: number): Pt[] {
  // boundary pixels
  const edges: number[] = [];
  for (let y = 0; y < H; y++) {
    const row = y * W;
    for (let x = 0; x < W; x++) {
      const i = row + x;
      if (!inside(i)) continue;
      if (
        x === 0 || y === 0 || x === W - 1 || y === H - 1 ||
        !inside(i - 1) || !inside(i + 1) || !inside(i - W) || !inside(i + W)
      ) edges.push(i);
    }
  }
  if (edges.length === 0) return [];

  // spatial grid for nearest-unvisited lookup
  const CELL = 3, inv = 1 / CELL, R2 = 3 * 3;
  const grid = new Map<number, number[]>();
  const gkey = (cx: number, cy: number) => cx * 46341 + cy;
  for (const i of edges) {
    const k = gkey(Math.floor((i % W) * inv), Math.floor(((i / W) | 0) * inv));
    let a = grid.get(k); if (!a) { a = []; grid.set(k, a); } a.push(i);
  }
  const visited = new Uint8Array(W * H);
  const nearest = (x: number, y: number) => {
    const cx = Math.floor(x * inv), cy = Math.floor(y * inv);
    let best = -1, bd = R2 + 1;
    for (let gx = cx - 1; gx <= cx + 1; gx++)
      for (let gy = cy - 1; gy <= cy + 1; gy++) {
        const a = grid.get(gkey(gx, gy));
        if (!a) continue;
        for (const j of a) {
          if (visited[j]) continue;
          const d = (j % W - x) ** 2 + (((j / W) | 0) - y) ** 2;
          if (d < bd) { bd = d; best = j; }
        }
      }
    return bd <= R2 ? best : -1;
  };

  // chain boundary pixels into ordered contours
  const chains: Pt[][] = [];
  for (const start of edges) {
    if (visited[start]) continue;
    const chain: Pt[] = [];
    let cur = start;
    while (cur >= 0) {
      visited[cur] = 1;
      const x = cur % W, y = (cur / W) | 0;
      chain.push([x, y]);
      cur = nearest(x, y);
    }
    chains.push(chain);
  }

  // arc length per chain, distribute count proportionally
  const lens = chains.map((ch) => {
    let L = 0;
    for (let i = 1; i < ch.length; i++) L += Math.hypot(ch[i][0] - ch[i - 1][0], ch[i][1] - ch[i - 1][1]);
    return Math.max(L, 1e-3);
  });
  const total = lens.reduce((a, b) => a + b, 0);
  const alloc = chains.map((_, i) => Math.max(1, Math.round((count * lens[i]) / total)));
  let gi = 0;
  for (let i = 1; i < alloc.length; i++) if (alloc[i] > alloc[gi]) gi = i;
  alloc[gi] = Math.max(1, alloc[gi] + (count - alloc.reduce((a, b) => a + b, 0)));

  // resample each chain evenly by arc length
  const out: Pt[] = [];
  for (let c = 0; c < chains.length && out.length < count; c++) {
    const ch = chains[c], m = alloc[c];
    if (m <= 0) continue;
    if (ch.length === 1) { for (let k = 0; k < m; k++) out.push([ch[0][0], ch[0][1]]); continue; }
    const cum = [0];
    for (let i = 1; i < ch.length; i++) cum.push(cum[i - 1] + Math.hypot(ch[i][0] - ch[i - 1][0], ch[i][1] - ch[i - 1][1]));
    const Lc = cum[cum.length - 1];
    for (let k = 0; k < m; k++) {
      const t = (m === 1 ? 0.5 : k / m) * Lc;
      let seg = 1;
      while (seg < cum.length && cum[seg] < t) seg++;
      if (seg >= cum.length) seg = cum.length - 1;
      const a = cum[seg - 1], b = cum[seg];
      const f = b > a ? (t - a) / (b - a) : 0;
      const p0 = ch[seg - 1], p1 = ch[seg];
      out.push([p0[0] + (p1[0] - p0[0]) * f, p0[1] + (p1[1] - p0[1]) * f]);
    }
  }
  while (out.length < count) out.push(out.length ? out[out.length - 1] : [W / 2, H / 2]);
  return out.slice(0, count);
}

function cloudFromImageData(
  img: ImageData,
  count: number,
  opts: { colorize: Colorize; alphaGate?: number; lumaGate?: number; mode: RenderMode; targetH?: number }
): Cloud {
  const { width: W, height: H, data: px } = img;
  const { colorize, alphaGate = 50, lumaGate = 0, mode, targetH = 4.5 } = opts;

  // detect a uniform filled background (e.g. an SVG's white <rect>)
  const corners = [0, W - 1, (H - 1) * W, (H - 1) * W + (W - 1)].map((i) => i * 4);
  const allOpaque = corners.every((o) => px[o + 3] > 200);
  let uniformBg = false, bgR = 0, bgG = 0, bgB = 0;
  if (allOpaque) {
    let maxd = 0;
    for (let a = 0; a < corners.length; a++)
      for (let b = a + 1; b < corners.length; b++) {
        const oa = corners[a], ob = corners[b];
        const d = (px[oa] - px[ob]) ** 2 + (px[oa + 1] - px[ob + 1]) ** 2 + (px[oa + 2] - px[ob + 2]) ** 2;
        if (d > maxd) maxd = d;
      }
    if (maxd < 900) {
      uniformBg = true;
      for (const o of corners) { bgR += px[o]; bgG += px[o + 1]; bgB += px[o + 2]; }
      bgR /= 4; bgG /= 4; bgB /= 4;
    }
  }
  const BG_D2 = 1500;
  const inside: InsideFn = (i) => {
    const o = i * 4;
    if (px[o + 3] <= alphaGate) return false;
    if (uniformBg) {
      const d = (px[o] - bgR) ** 2 + (px[o + 1] - bgG) ** 2 + (px[o + 2] - bgB) ** 2;
      if (d < BG_D2) return false;
    } else if (lumaGate > 0) {
      if (px[o] * 0.3 + px[o + 1] * 0.59 + px[o + 2] * 0.11 < lumaGate) return false;
    }
    return true;
  };

  let pts: Pt[] = mode === "outline"
    ? collectOutline(inside, W, H, count)
    : collectFill(inside, W, H, count).map((i) => [i % W, (i / W) | 0] as Pt);
  if (pts.length === 0) pts = collectFill(inside, W, H, count).map((i) => [i % W, (i / W) | 0] as Pt);
  const L = Math.max(1, pts.length);

  const positions = new Float32Array(count * 3);
  const colors = new Float32Array(count * 3);
  // size by height (consistent letter/shape height), width follows aspect, capped
  let sh = targetH, sw = targetH * (W / H);
  const maxW = 16;
  if (sw > maxW) { const k = maxW / sw; sw *= k; sh *= k; }

  for (let n = 0; n < count; n++) {
    const p = L >= count ? pts[Math.floor((n * L) / count)] : pts[n % L];
    const fx = p[0], fy = p[1];
    positions[n * 3] = (fx / W - 0.5) * sw;
    positions[n * 3 + 1] = (0.5 - fy / H) * sh;
    positions[n * 3 + 2] = 0; // flat

    const ci = Math.min(H - 1, Math.max(0, Math.floor(fy))) * W + Math.min(W - 1, Math.max(0, Math.floor(fx)));
    const r = px[ci * 4] / 255, g = px[ci * 4 + 1] / 255, b = px[ci * 4 + 2] / 255;
    if (colorize === "pixel") {
      colors[n * 3] = r; colors[n * 3 + 1] = g; colors[n * 3 + 2] = b;
    } else if (colorize === "pixelBright" && r * 0.3 + g * 0.59 + b * 0.11 > 0.16) {
      colors[n * 3] = r; colors[n * 3 + 1] = g; colors[n * 3 + 2] = b;
    } else {
      _c.setHSL(((positions[n * 3] / sw) + 0.5) * 0.8 + 0.02, 0.85, 0.58);
      colors[n * 3] = _c.r; colors[n * 3 + 1] = _c.g; colors[n * 3 + 2] = _c.b;
    }
  }
  return { positions, colors };
}

/** Typed text → flat swarm, in the chosen (installed) font. */
export function sampleText(text: string, count: number, font = "Arial", mode: RenderMode = "outline"): Cloud {
  const label = text.length ? text : " ";
  const fontPx = 220;
  const family = `"${font}", sans-serif`;
  const probe = document.createElement("canvas").getContext("2d")!;
  probe.font = `${fontPx}px ${family}`;
  const pad = 60;
  const w = Math.max(1, Math.ceil(probe.measureText(label).width)) + pad * 2;
  const h = fontPx + pad * 2;

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d")!;
  ctx.clearRect(0, 0, w, h);
  ctx.font = `${fontPx}px ${family}`;
  ctx.fillStyle = "#fff";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(label, w / 2, h / 2);

  return cloudFromImageData(ctx.getImageData(0, 0, w, h), count, { colorize: "rainbow", alphaGate: 60, mode });
}

function svgToDataUrl(svg: string): string {
  if (!/<svg[^>]*\bwidth\s*=/.test(svg) || !/<svg[^>]*\bheight\s*=/.test(svg)) {
    const vb = svg.match(/viewBox\s*=\s*["']([\d.eE+\s-]+)["']/);
    let w = 512, h = 512;
    if (vb) {
      const p = vb[1].trim().split(/[\s,]+/).map(Number);
      if (p.length === 4 && p[2] > 0 && p[3] > 0) { w = p[2]; h = p[3]; }
    }
    svg = svg.replace(/<svg\b/i, `<svg width="${w}" height="${h}"`);
  }
  return "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svg);
}

function rasterizeToCloud(url: string, count: number, colorize: Colorize, lumaGate: number, mode: RenderMode): Promise<Cloud> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const maxDim = 420;
      const scale = Math.min(maxDim / img.width, maxDim / img.height, 1) || 1;
      const w = Math.max(1, Math.round(img.width * scale));
      const h = Math.max(1, Math.round(img.height * scale));
      const cv = document.createElement("canvas");
      cv.width = w; cv.height = h;
      const ctx = cv.getContext("2d")!;
      ctx.clearRect(0, 0, w, h);
      ctx.drawImage(img, 0, 0, w, h);
      try {
        resolve(cloudFromImageData(ctx.getImageData(0, 0, w, h), count, { colorize, lumaGate, alphaGate: 40, mode, targetH: 7 }));
      } catch (e) {
        reject(e instanceof Error ? e : new Error(String(e)));
      }
    };
    img.onerror = () => reject(new Error("Could not load that file as an image."));
    img.src = url;
  });
}

/** SVG string → flat swarm (rainbow-tinted so dark line-art stays visible). */
export function sampleSVG(svg: string, count: number, mode: RenderMode = "outline"): Promise<Cloud> {
  return rasterizeToCloud(svgToDataUrl(svg), count, "pixelBright", 0, mode);
}

/** Image (object URL or data URL) → flat swarm keeping its own colors. */
export function sampleImageFile(url: string, count: number, mode: RenderMode = "outline"): Promise<Cloud> {
  return rasterizeToCloud(url, count, "pixel", 18, mode);
}

/** Curated Windows system fonts (fallback when Local Font Access is unavailable). */
export const CURATED_FONTS = [
  "Arial", "Bahnschrift", "Calibri", "Cambria", "Candara", "Comic Sans MS",
  "Consolas", "Constantia", "Corbel", "Courier New", "Franklin Gothic Medium",
  "Gabriola", "Georgia", "Impact", "Ink Free", "Lucida Console", "Segoe UI",
  "Segoe Print", "Sitka", "Tahoma", "Times New Roman", "Trebuchet MS", "Verdana",
];

/** Ask the browser for the user's actually-installed fonts (needs a gesture + permission). */
export async function queryInstalledFonts(): Promise<string[]> {
  const q = (window as unknown as { queryLocalFonts?: () => Promise<Array<{ family: string }>> }).queryLocalFonts;
  if (!q) throw new Error("This browser can't list system fonts.");
  const fonts = await q();
  return Array.from(new Set(fonts.map((f) => f.family))).sort((a, b) => a.localeCompare(b));
}
